import { Divider, Empty, Image, Modal, Table, TableColumnType } from 'antd';
import { PecaAiProduct, usePecaAiContext } from '../../PecaAiProvider';
import { DeleteIcon, Group } from '@/components/_common';
import formatterPrice from '@/utils/number/formatterPrice';
import {
  PECAAI_BASKET_DETAIL,
  usePecaAiRemoveProductInBasket,
} from '@/core/domain/pecaAi/basket/pecaAiBasket.hooks';
import { useWorkorderComplementSyncWithPecaAi } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import { useWorkorderDetailContext } from '../../../WorkorderDetailProvider';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { WORKORDER_ITEMS_QUERY_KEY } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import { decimalToTime } from '@/utils/functions/decimalToTime';

interface Props {
  hasBasket: boolean;
}

export default function PecaAiListProduct(props: Props) {
  const {
    products,
    removeProduct: removeNewProduct,
    basketId,
    cnpj,
  } = usePecaAiContext();

  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const newProducts = products.filter((product) => !product.inBasket);
  const productsInBasket = products.filter((product) => product.inBasket);
  const syncWithPecaAi = useWorkorderComplementSyncWithPecaAi();

  const removeProduct = usePecaAiRemoveProductInBasket();

  const queryChanel = useQueryChanel();

  const columns: TableColumnType<PecaAiProduct>[] = [
    {
      dataIndex: 'description',
      title: 'Descrição/Observação',
      render: (_, item) => {
        const info = (
          <>
            <span style={{ display: 'block' }}>{item.description}</span>
            <span style={{ display: 'block' }}>{item.observation}</span>
          </>
        );

        if (item.inBasket) {
          return info;
        }

        return (
          <Group gap={10}>
            <Image preview={false} src={item.image} width={40} height={40} />
            {info}
          </Group>
        );
      },
    },
    { dataIndex: 'partNumber', title: 'Cód. peça' },
    {
      dataIndex: 'brand',
      title: 'Marca',
      render: (_, item) => item.brand || '-',
    },
    { dataIndex: 'amount', title: 'Qtde.', align: 'right' },
    {
      dataIndex: 'unitPrice',
      title: 'Preç. Unit.',
      align: 'right',
      render: (value: number) => formatterPrice(value),
    },
    {
      dataIndex: 'shippingPrice',
      title: 'Frete',
      align: 'right',
      render: (value: number) => (value ? formatterPrice(value) : '-'),
    },
    {
      dataIndex: 'totalPrice',
      title: 'Preç. Total',
      align: 'right',
      render: (value: number) => <strong>{formatterPrice(value)}</strong>,
    },
  ];

  const columnDeadlineNewProduct: TableColumnType<PecaAiProduct>[] = [
    {
      dataIndex: 'deadlineHours',
      title: 'Entrega',
      align: 'right',
      render: (value: number) => decimalToTime(value),
    },
  ];

  const columnActionBasket: TableColumnType<PecaAiProduct> = {
    title: 'Ação',
    render: (_, item) => (
      <Group position="center">
        <DeleteIcon
          onClick={() =>
            Modal.confirm({
              title: 'Remoção do produto',
              content: `Deseja remover o produto "${item.description}" da cesta?`,
              okText: 'Remover',
              onOk: async () => {
                if (basketId && cnpj && complement && workorder) {
                  await removeProduct.mutateAsync({
                    document: cnpj,
                    basketId,
                    productId: item.productId,
                  });

                  await syncWithPecaAi.mutateAsync({
                    complementId: complement.id,
                    workOrderId: workorder.id,
                  });

                  queryChanel({
                    invalidateQueries: [
                      [PECAAI_BASKET_DETAIL],
                      [WORKORDER_ITEMS_QUERY_KEY],
                    ],
                  });
                }
              },
            })
          }
        />
      </Group>
    ),
    width: 100,
    align: 'center',
  };

  const columnActionNewBasket: TableColumnType<PecaAiProduct> = {
    title: 'Ação',
    render: (_, item) => (
      <Group position="center">
        <DeleteIcon
          onClick={() =>
            Modal.confirm({
              title: 'Remoção do produto',
              content: `Deseja remover o produto "${item.description}" da cesta?`,
              okText: 'Remover',
              onOk: () => removeNewProduct(item.productId),
            })
          }
        />
      </Group>
    ),
    width: 100,
    align: 'center',
  };

  return (
    <>
      <Divider />

      {!props.hasBasket && !!newProducts.length && (
        <>
          <strong style={{ fontSize: '1.1rem' }}>Novos produtos</strong>
          <Table
            dataSource={newProducts}
            columns={[
              ...columns,
              ...columnDeadlineNewProduct,
              columnActionNewBasket,
            ]}
            pagination={false}
          />
        </>
      )}

      {props.hasBasket && !!productsInBasket.length && (
        <>
          <strong style={{ fontSize: '1.1rem' }}>Produtos na cesta</strong>
          <Table
            dataSource={productsInBasket}
            columns={[...columns, columnActionBasket]}
            pagination={false}
          />
        </>
      )}

      {!productsInBasket.length && !newProducts.length && (
        <Empty description="Não há produtos na cesta. Informe a quantidade e selecione um produto" />
      )}
    </>
  );
}
